import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useSidebarStore from '@entities/ui/model/useSidebarStore';
import useUnsavedLeaveStore from '@shared/model/useUnsavedLeaveStore';
import useIsMobile from '@shared/hooks/useIsMobile';
import BoxIcon from '@shared/assets/images/box_icon.svg';
import ChevronRightIcon from '@shared/assets/images/chevron-right_icon.svg';
import InboxIcon from '@shared/assets/images/inbox-01_icon.svg';
import SettingsIcon from '@shared/assets/images/settings-01_icon.svg';
import UmbrellaIcon from '@shared/assets/images/umbrella-03_icon.svg';

import styles from './AdminSidebar.module.scss';

/**
 * URL 경로를 기반으로 현재 활성 메뉴 키를 반환합니다.
 * @param {string} path - 확인할 URL 경로
 * @returns {string|null} - 활성 메뉴 키 또는 null
 */
function getActiveMenuKey(path) {
  if (path.startsWith('/admin/documents')) {
    return 'documents';
  }
  if (path.startsWith('/admin/vacation')) {
    return 'vacation';
  }
  if (path.startsWith('/admin/employees')) {
    return 'employees';
  }
  if (path.startsWith('/admin/organization')) {
    return 'organization';
  }
  if (
    path.startsWith('/admin/forms/document') ||
    path.startsWith('/admin/forms/approval-line-reference')
  ) {
    return 'forms';
  }
  return null;
}

/**
 * AdminSidebar 컴포넌트
 * - 관리자 전용 좌측 네비게이션 메뉴
 * - 기존 Sidebar의 아이콘을 재사용
 */
export default function AdminSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, toggle: toggleSidebar } = useSidebarStore();
  const isMobile = useIsMobile();
  const prevPathRef = useRef(location.pathname);

  // 컴포넌트가 로드될 때 현재 URL을 기반으로 메뉴의 초기 상태를 설정
  const [openMenus, setOpenMenus] = useState(() => {
    const activeMenuKey = getActiveMenuKey(location.pathname);
    return {
      documents: activeMenuKey === 'documents',
      vacation: activeMenuKey === 'vacation',
      employees: activeMenuKey === 'employees',
      organization: activeMenuKey === 'organization',
      forms: activeMenuKey === 'forms',
    };
  });

  // URL이 변경될 때 메뉴 상태를 동기화
  useEffect(() => {
    const path = location.pathname;
    const prevPath = prevPathRef.current;

    // 같은 메뉴 그룹 내에서 이동하는 경우, 상태 업데이트 불필요
    if (getActiveMenuKey(path) === getActiveMenuKey(prevPath)) {
      prevPathRef.current = path;
      return;
    }

    // 다른 메뉴 그룹으로 이동한 경우, 상태 업데이트
    const targetMenuKey = getActiveMenuKey(path);
    setOpenMenus({
      documents: targetMenuKey === 'documents',
      vacation: targetMenuKey === 'vacation',
      employees: targetMenuKey === 'employees',
      organization: targetMenuKey === 'organization',
      forms: targetMenuKey === 'forms',
    });

    prevPathRef.current = path;
  }, [location.pathname]);

  /**
   * 드롭다운 메뉴의 열림/닫힘 상태를 토글합니다. (아코디언 방식)
   * @param {string} menuKey - 토글할 메뉴의 키
   */
  function toggleMenu(menuKey) {
    setOpenMenus((prev) => {
      const isAlreadyOpen = prev[menuKey];
      // 모든 메뉴를 닫고, 현재 메뉴의 상태만 반전시킴
      return {
        documents: false,
        vacation: false,
        employees: false,
        organization: false,
        forms: false,
        [menuKey]: !isAlreadyOpen,
      };
    });
  }

  // 저장 없이 이동 시 확인 (문서 양식 관리 / 결재라인 안내 관리)
  const requestLeave = useUnsavedLeaveStore((state) => state.requestLeave);

  /**
   * 지정된 경로로 페이지를 이동합니다.
   * - 문서 양식 관리·결재라인 안내 관리에서 변경사항이 있으면 확인 모달 후 이동
   * @param {string} path - 이동할 라우트 경로
   * @param {Event} event - 이벤트 객체 (선택적)
   */
  function handleMenuClick(path, event) {
    // 하위메뉴 클릭 시 이벤트 전파 방지
    if (event) {
      event.stopPropagation();
    }

    /**
     * 페이지 이탈 요청 이벤트 생성
     *
     * - 실제 navigate를 직접 호출하지 않고
     *   전역 이벤트(app:leave-request)를 먼저 발생시킨다.
     *
     * - 목적:
     *   현재 페이지가 "작성 중(dirty)" 상태인지 스스로 판단할 수 있도록
     *   제어권을 페이지(OrganizationManagementPage 등)에게 위임
     *
     * - cancelable: true
     *   → 수신자가 e.preventDefault()를 호출하면
     *     이 이동 요청은 취소될 수 있다.
     */
    const navEvent = new CustomEvent('app:leave-request', {
      cancelable: true,
      detail: {
        type: 'navigation',
        path,
      },
    });

    /**
     * 전역 이벤트 디스패치
     *
     * - 이 시점에서:
     *   - 현재 페이지가 dirty 하지 않으면 → 아무 일도 안 일어남
     *   - dirty 하면 → confirm Alert를 띄우고 preventDefault 호출
     */
    window.dispatchEvent(navEvent);

    /**
     *  이동이 차단되었는지 확인
     *
     * - OrganizationManagementPage 등에서
     *   e.preventDefault()가 호출되었다면
     *   defaultPrevented === true
     *
     * - 이 경우:
     *   → confirm 결과가 나올 때까지
     *     사이드바에서는 아무 것도 하지 않음
     */
    if (navEvent.defaultPrevented) {
      return;
    }
    /**
     *  이동이 허용된 경우에만 실제 라우팅 수행
     *
     * - dirty 하지 않거나
     * - confirm 에서 "예"를 눌러 강제로 이동을 허용한 경우
     */

    requestLeave(function () {
      navigate(path);
    });
  }

  /**
   * 현재 URL 경로가 주어진 경로와 일치하는지 확인합니다.
   * @param {string} path - 비교할 라우트 경로
   * @returns {boolean} 현재 경로와 일치하면 true, 아니면 false
   */
  function isActiveMenu(path) {
    return location.pathname === path;
  }

  /**
   * 문서 양식 관리 메뉴 활성 여부 (목록/추가/수정 화면 모두 포함)
   * - /admin/forms/document, /admin/forms/document/add, /admin/forms/document/edit/:id
   */
  function isDocumentFormsActive() {
    const path = location.pathname;
    return path === '/admin/forms/document' || path.startsWith('/admin/forms/document/');
  }

  return (
    <>
      {/* 사이드바 토글 버튼 - 사이드바 바깥에 위치하여 항상 표시 */}
      <button
        type="button"
        className={`${styles.toggleButton} ${!isOpen ? styles.toggleButtonClosed : ''}`}
        onClick={toggleSidebar}
        aria-label={isOpen ? '사이드바 닫기' : '사이드바 열기'}
      >
        <img
          src={ChevronRightIcon}
          alt=""
          className={`${styles.toggleIcon} ${isOpen ? styles.toggleIconOpen : ''}`}
        />
      </button>

      <aside className={`${styles.sidebar} ${!isOpen ? styles.sidebarClosed : ''}`}>
        <nav className={styles.nav}>
          {/* 문서관리 */}
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={`${styles.menuItem} ${isActiveMenu('/admin/documents') ? styles.menuItemActive : ''}`}
              onClick={() => handleMenuClick('/admin/documents')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={InboxIcon} alt="문서관리" />
                </span>
                <span className={styles.menuText}>문서관리</span>
              </span>
            </button>
          </div>

          {/* 휴가관리 */}
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={`${styles.menuItem} ${isActiveMenu('/admin/vacation') ? styles.menuItemActive : ''}`}
              onClick={() => handleMenuClick('/admin/vacation')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={UmbrellaIcon} alt="휴가관리" />
                </span>
                <span className={styles.menuText}>휴가관리</span>
              </span>
            </button>
          </div>

          {/* 직원관리 */}
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={`${styles.menuItem} ${isActiveMenu('/admin/employees') ? styles.menuItemActive : ''}`}
              onClick={() => handleMenuClick('/admin/employees')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={SettingsIcon} alt="직원관리" />
                </span>
                <span className={styles.menuText}>직원관리</span>
              </span>
            </button>
          </div>

          {/* 조직관리 */}
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={`${styles.menuItem} ${isActiveMenu('/admin/organization') ? styles.menuItemActive : ''}`}
              onClick={() => handleMenuClick('/admin/organization')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={SettingsIcon} alt="조직관리" />
                </span>
                <span className={styles.menuText}>조직관리</span>
              </span>
            </button>
          </div>

          {/* 양식관리 */}
          <div className={styles.menuGroup}>
            <button type="button" className={styles.menuItem} onClick={() => toggleMenu('forms')}>
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={BoxIcon} alt="양식관리" />
                </span>
                <span className={styles.menuText}>양식관리</span>
              </span>
              <span className={`${styles.chevron} ${openMenus.forms ? styles.chevronOpen : ''}`} />
            </button>
            <div className={`${styles.subMenu} ${openMenus.forms ? styles.subMenuOpen : ''}`}>
              <button
                type="button"
                className={`${styles.subMenuItem} ${isDocumentFormsActive() ? styles.subMenuItemActive : ''}`}
                onClick={(e) => handleMenuClick('/admin/forms/document', e)}
              >
                <span className={styles.subMenuInner}>
                  <span className={styles.indicator} />
                  <span className={styles.subMenuText}>문서 양식 관리</span>
                </span>
              </button>
              <button
                type="button"
                className={`${styles.subMenuItem} ${isActiveMenu('/admin/forms/approval-line-reference') ? styles.subMenuItemActive : ''}`}
                onClick={(e) => handleMenuClick('/admin/forms/approval-line-reference', e)}
              >
                <span className={styles.subMenuInner}>
                  <span className={styles.indicator} />
                  <span className={styles.subMenuText}>결재라인 안내 관리</span>
                </span>
              </button>
            </div>
          </div>
          {/* 문의사항관리 */}
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={`${styles.menuItem} ${isActiveMenu('/admin/inquiry') ? styles.menuItemActive : ''}`}
              onClick={() => handleMenuClick('/admin/inquiry')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={InboxIcon} alt="문의사항관리" />
                </span>
                <span className={styles.menuText}>문의사항 관리</span>
              </span>
            </button>
          </div>
        </nav>

        {/* 사이드바 푸터 */}
        <footer className={styles.sidebarFooter}>
          <button type="button" className={`${styles.footerLink} ${styles.hidden}`}>
            버전 정보
          </button>
        </footer>
      </aside>
    </>
  );
}
